import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './layout.module.scss';
import { Link, useLocation } from 'react-router-dom';
import { useConversations } from '../../../hooks/chat';
import menu from './menus/menu';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { setDrawer, setExpandDrawer } from '../../../redux/reducers/common';
import { LayoutContext } from '../../../Providers/LayoutProvider';
import my_dashboard_menu from './menus/my_dashboard_menu';
import tester_dashboard_menu from './menus/tester_dashboard_menu';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PermissionProvider } from '../../../hoc/withPermissions';
import SidebarExpandIcon from '../../SvgIcons/SidebarExpandIcon';
import { SIEMBAGPT_ENABLED } from '../../../utils/constants';
import BetaIcon from '../../SvgIcons/BetaIcon';
import { useValidateLogin } from '../../../hooks/auth';
import { useAppMode } from '../../../Providers/AppModeProvider';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UpgradeTooltip from '../../UpgradeTooltip';

const Sidebar = ({ from }) => {
  const collapseBtnRef = useRef();
  // undefined = follow route; number = force open; null = force closed
  const [collapseIndex, setCollapseIndex] = useState(undefined);
  const [expandSidebar, updateExpandSidebar] = useState(true);
  const [expandHoverSidebar, updateExpandHoverSidebar] = useState(false);
  const { pathname: path } = useLocation();
  const dispatch = useDispatch();
  const { openDrawer, expandDrawer } = useSelector((state) => state.common);
  const { integrationsEnabled } = useSelector((state) => state.auth);
  const value = useContext(LayoutContext);
  const { APP_MODE, dateExpired } = useAppMode();
  const { data } = useValidateLogin();

  const { data: personalData } = useConversations({ type: 'personal', enabled: true, search: '' });
  const { data: channelData } = useConversations({ type: 'channels', enabled: true, search: '' });

  const totalUnreadCount =
    personalData?.pages[0]?.totalUnreadCount + channelData?.pages[0]?.totalUnreadCount || 0;

  useEffect(() => {
    if (expandDrawer !== undefined) {
      updateExpandSidebar(expandDrawer);
    } else {
      updateExpandSidebar(true);
      dispatch(setExpandDrawer(true));
    }
  }, [expandDrawer, dispatch]);

  useEffect(() => {
    if (openDrawer) {
      updateExpandSidebar(openDrawer);
      updateExpandHoverSidebar(false);
      dispatch(setExpandDrawer(openDrawer));
    }
  }, [openDrawer, dispatch]);

  // Trial restrictions
  const isCustomer = data?.user?.user_type === 'customer';
  const isTrialMode = APP_MODE === 'genptbeta';
  const isTrialRestricted = isTrialMode && isCustomer;

  const isDisabled = (label, isChild = false) => {
    if (!isTrialRestricted) return false;
    if (dateExpired) return true;
    return !isChild && label !== 'GenPT' && label !== 'Integrations';
  };

  // Build menu for current dashboard and apply GenPT gating
  let menuList = getMenu(value.dashboard);
  if (
    SIEMBAGPT_ENABLED === 'false' ||
    SIEMBAGPT_ENABLED === '' ||
    !integrationsEnabled?.is_genpt_enabled
  ) {
    menuList = menuList.filter((obj) => !obj?.path?.includes('genpt'));
  }

  // Which parent group should be open based on the current route?
  const routeIndex = useMemo(() => {
    let idx = null;
    menuList?.forEach((menuItem, i) => {
      const isChildMatch = menuItem?.child?.some((el) => path.startsWith(el?.path));
      if (isChildMatch && !isDisabled(menuItem.label)) idx = i;
    });
    return idx;
    // deps: if trial flags change, the disabled state can change which group should auto-open
  }, [menuList, path, isTrialRestricted, dateExpired]);

  // Final open group: manual override wins; otherwise follow route
  const openIndex = collapseIndex === undefined ? routeIndex : collapseIndex;

  // When the route changes, go back to "follow the route" mode
  useEffect(() => {
    setCollapseIndex(undefined);
  }, [path]);

  const renderSingleMenu = (singleMenu, index, isChild = false) => {
    const betaFeature = ['EASM', 'Mission Control', 'GenPT'];
    const showBetaIcon =
      betaFeature.includes(singleMenu.label) &&
      !(APP_MODE === 'genptbeta' || data?.user?.user_type === 'provider');

    const disabled = isDisabled(singleMenu.label, isChild);

    return (
      <div className={`flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}>
        <div className="inline-block w-[39px]">{singleMenu?.icon && <singleMenu.icon />}</div>

        {singleMenu.label}

        {showBetaIcon && (
          <span className="ml-2">
            <BetaIcon />
          </span>
        )}

        {disabled && (
          <UpgradeTooltip>
            <ArrowUpwardIcon
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', fontSize: '16px' }}
              className="ml-2 text-xs rounded text-tertiary-400 rounded-full"
            />
          </UpgradeTooltip>
        )}

        {!disabled && singleMenu.label === 'Chat' && totalUnreadCount > 0 && (
          <span className="bg-white py-[2px] px-[8px] text-secondary-700 text-xs rounded-full mx-[15px] my-[3px] font-semibold">
            {totalUnreadCount}
          </span>
        )}

        {!disabled && singleMenu?.child && (
          <>
            {index === openIndex ? (
              <ExpandLessIcon className="!align-bottom" />
            ) : (
              <ExpandMoreIcon className="!align-bottom" />
            )}
          </>
        )}
      </div>
    );
  };

  const renderMenu = (items, child = false) => {
    return items?.map((item, index) => {
      let active = false,
        parentActive = false;

      const disabled = isDisabled(item.label, child);

      if (item?.child?.some((el) => path.startsWith(el?.path))) {
        parentActive = true;
      }

      if (data?.user?.role === 'customer_leader' && item?.label === 'My Dashboard') {
        return null;
      }

      active = path.startsWith(item.path);

      return (
        <PermissionProvider key={index} roles={item?.roles}>
          <li
            className={`${active && styles.active} ${parentActive && styles.parentActive} 
                        ${child && styles.child}`}
          >
            <>
              {item?.path && !disabled ? (
                <Link
                  onClick={() => {
                    dispatch(setDrawer(false));
                    if (!child) setCollapseIndex(undefined); // let route decide after navigation
                  }}
                  to={item?.path}
                  className={styles.menu}
                >
                  {renderSingleMenu(item, index, child)}
                </Link>
              ) : (
                <div
                  onClick={() => {
                    if (disabled) return;
                    // Toggle this parent: if it's currently open, force closed (null); else force open (index)
                    setCollapseIndex((prev) => {
                      const currentOpen = prev === undefined ? routeIndex : prev;
                      return currentOpen === index ? null : index;
                    });
                  }}
                  className={`${styles.menu} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  {renderSingleMenu(item, index, child)}
                </div>
              )}

              {item?.child && (
                <Collapse in={openIndex === index}>
                  <ul>{renderMenu(item.child, true)}</ul>
                </Collapse>
              )}
            </>
          </li>
        </PermissionProvider>
      );
    });
  };

  return (
    <div
      className={`${from === 'drawer' ? '' : styles.sidebar} no-scrollbar h-full ${
        expandSidebar || expandHoverSidebar ? styles.sidebarExpanded : styles.sidebarCollapse
      }`}
      onMouseEnter={(e) => {
        // e.target.className doesn't always return a string, hence we check its type first
        // otherwise, we get a "TypeError: e.target.className.includes is not a function" error
        // for e.g. when e.target is a SVG, its className is SVGAnimatedString
        if (typeof e.target.className != 'string' || !e.target.className.includes('collapse-ele')) {
          if (!expandSidebar) {
            updateExpandHoverSidebar(true);
            dispatch(setExpandDrawer(true));
          }
        }
      }}
      onMouseLeave={() => {
        if (!expandSidebar && expandDrawer) {
          updateExpandHoverSidebar(false);
          dispatch(setExpandDrawer(false));
        }
      }}
    >
      <div
        className={`${styles.sidebarCollapseBtn} collapse-ele`}
        ref={collapseBtnRef}
        onClick={() => {
          if (openDrawer) {
            dispatch(setDrawer(false));
          }
          else {
            const nextExpanded = !expandSidebar;
            updateExpandSidebar(nextExpanded);
            updateExpandHoverSidebar(false);
            dispatch(setExpandDrawer(nextExpanded));
            sessionStorage.setItem('drawerCollapsed', !nextExpanded);
          }
        }}
      >
        <span
          className={`${!expandSidebar || expandHoverSidebar ? styles.collapseIcon : ''} ${
            styles.expandIcon
          }`}
        >
          <SidebarExpandIcon />
        </span>
      </div>
      <ul id="sidebarContainer">{renderMenu(menuList)}</ul>
    </div>
  );
};

Sidebar.propTypes = {
  from: PropTypes.string,
};

export default Sidebar;

// Helper
const getMenu = (dashboard) => {
  switch (dashboard) {
    case 'MY_DASHBOARD':
      return my_dashboard_menu;
    case 'TESTER_DASHBOARD':
      return tester_dashboard_menu;
    case 'ADMIN_DASHBOARD':
    default:
      return menu;
  }
};
